import { OFFLINE_SESSION_MAX_AGE_DAYS, type OfflineRejectReason, type SseEvent } from "@redchain/contracts";
import type { AutosaveNewMon, AutosaveRareFound, LinkEndOutcome } from "./worker/protocol";
import { formatRcd, formatRcdDelta, formatSol } from "./overlay/format";

/** UI strings (English only at launch; every user-facing sentence lives here). */
export const t = {
  idle: (ago: string) => `Last synced ${ago}`,
  uploading: "Saving to cloud…",
  verifying: "Verifying save…",
  syncing: (done: number, total: number) => `Updating on-chain (${done} of ${total})`,
  synced: "Synced",
  offline: "Offline. Your progress is kept on this device and will be uploaded when the connection returns.",
  offlineAdopted: (live: boolean) =>
    live ? "You're back online. Progress from offline play is uploading now." : "Progress from offline play was uploaded and is being verified.",
  offlineDiscarded: (reason: OfflineRejectReason, live: boolean) => {
    const why =
      reason === "base_changed" || reason === "session_open_elsewhere"
        ? " because your game changed on another device"
        : reason === "too_old"
          ? ` because it is more than ${OFFLINE_SESSION_MAX_AGE_DAYS} days old`
          : reason === "rules_changed"
            ? " because the game was updated while you were offline"
            : "";
    return `Progress from offline play couldn't be kept${why}.${live ? " Reload to continue from your latest save." : ""}`;
  },
  rejected: (when: string) => `This save couldn't be verified. Your last verified save is from ${when}.`,
  superseded: "This game was opened somewhere else.",
  ownershipChanged: (name: string) => `${name} was transferred out of this game. It will be removed the next time you load.`,
  ownershipArrived: (name: string) => `${name} arrived in this game. It will appear the next time you load.`,
  mintsNeedRcd: (mons: number, rcd: string) =>
    `${mons} Pokémon waiting to mint. Minting is paid from your in-game money, then from your wallet up to the limit you allow; ${formatRcd(rcd)} more is needed. They mint at your next save.`,
  mintsFromWallet: (mons: number) => `${mons} Pokémon waiting to mint. Your next save pays for them from your wallet.`,
  allowWallet: (rcd: string) => `Let the game spend up to ${formatRcd(rcd)} from my wallet`,
  mintDepositShort: (rcd: string) => `${formatRcd(rcd)} short in wallet`,
  allowanceHelp:
    "Spending limit: when your in-game money can't cover a new Pokémon, your next save pays the rest from your wallet's ₽, up to this limit. No signature per mint. The ₽ only ever goes into your own in-game money. Set 0 to turn it off.",
  mintsPoolPaused: (mons: number) => `${mons} Pokémon waiting to mint. Minting is paused while the mint pool refills.`,
  needsSolItems: (lamports: string) => `Rare items waiting to record. Add ${formatSol(lamports)} SOL`,
  vaultFunded: "Save to record your waiting rare items",
  released: (name: string, lamports: string) => `${name} was released. ${formatSol(lamports)} SOL returned to your wallet.`,
  feeDebt: (rcd: string) => `${formatRcd(rcd)} in fees will be taken from your next winnings.`,
  glitch: "Items, money and Pokémon gained through a glitch aren't minted. Items and Pokémon stay in your game; glitch money lasts until you next load.",
  rcdHelp: "Claimed RCD leaves the game; deposit it back to spend it in-game. Your purse updates while you play.",
  depositAddressHelp: "Your ₽ deposit address. Send ₽ here from any wallet or exchange; it is added to your in-game money at your next save. No signature or SOL needed.",
  depositBreakdown: (relay: number, debt: number, net: number) => {
    const parts = [relay ? `the ${formatRcd(relay)} network fee` : "", debt ? `${formatRcd(debt)} in fees you owe` : ""].filter(Boolean);
    const lead = parts.join(" and ");
    return `${lead[0]!.toUpperCase()}${lead.slice(1)} ${parts.length > 1 ? "come" : "comes"} out of this; ${formatRcd(net)} goes into your purse.`;
  },
  itemsDuringPlay: "Claim between sessions so your bag doesn't change mid-game",
  walletItemsLoading: "Loading items...",
  walletItemsEmpty: "No rare items.",
  walletFeesHelp: "Paid from game money.",
  walletFeeDebt: (rcd: string) => `Owed ${formatRcd(rcd)}. Paid from next winnings.`,
  walletComingSoon: "Coming soon.",
  walletShopEmpty: "No stock yet.",
  walletMarketplaceHelp: "List, buy and sell items and Pokémon with other players.",
  walletMarketEmpty: "No listings yet.",
  walletMarketBuySoon: "Coming soon.",
  walletMarketSeller: (seller: string) => `By ${seller}`,
  walletMarketNothingToList: "No Pokémon to list.",
  walletMarketPriceError: "Enter RCD price.",
  walletMarketListSoon: "Coming soon.",
  walletRcdMarketHelp: "Buy and sell RCD directly through our liquidity pools.",
  walletRcdNoQuote: "Quotes coming soon.",
  walletRcdEnterAmount: "Enter an amount.",
  walletRcdLowSol: "Not enough SOL.",
  walletRcdLowBalance: "Not enough RCD.",
  walletRcdTradeSoon: "Trading soon.",
  midSessionClaim:
    "You're playing right now. Claiming takes the ₽ out of your purse in the game too; if you've spent more this session than your saved balance, the difference becomes debt repaid from future winnings. Claim anyway?",
  fees: (perSave: number, perAutosave: number, perMint: number) =>
    `Network fees are paid in ₽: ${formatRcd(perSave)} per save, ${formatRcd(perAutosave)} per auto-save, ${formatRcd(perMint)} per new Pokémon. They come out of your money and show at your next load.`,
  burnConfirm: (name: string, lamports: string) => `Burn ${name} permanently and get back ${formatSol(lamports)} SOL?`,
  burnConfirmRcd: (name: string, rcd: string) => `Burn ${name} permanently and get ${formatRcd(rcd)} in your in-game money?`,
  refundRcdHint: (rcd: string, lamports: string) => `Burn refund: ${formatRcd(rcd)} in game, or ${formatSol(lamports)} SOL`,
  refundRcdOnly: (rcd: string) => `Burn refund: ${formatRcd(rcd)} in game`,
  buyLevelsQuote: (from: number, to: number, rcd: string) => `Lv ${from} → ${to}: ${formatRcd(rcd)} from your in-game money`,
  buyLevelsConfirm: (name: string, from: number, to: number, rcd: string) =>
    `Raise ${name} from Lv ${from} to Lv ${to} for ${formatRcd(rcd)}? It shows the next time the game loads. Bought levels add no stat experience, and moves or evolutions come with the next level gained in battle.`,
  buyLevelsHelp: "Close the game first. Price per level rises with the level.",
  releasePref: (asRcd: boolean) =>
    asRcd
      ? "Pokémon you release in Bill's PC refund ₽ to your in-game money."
      : "Pokémon you release in Bill's PC refund SOL to your wallet.",
  lostWallet:
    "A lost wallet means lost assets. Moving to a new wallet needs your old wallet's signature, and there is no admin recovery. Export your key or use a wallet with a seed-phrase backup.",
};

/** Cable Club link play (§17). */
export const linkText = {
  privacy:
    "In a peer link your Cable Club save is sent to your opponent's browser, and theirs to yours. Choose “Run on the server” to keep it private.",
  armed: "Go to the Cable Club desk on the right in any Pokémon Center and save when the receptionist asks.",
  binding: "Sending your Cable Club save…",
  verifying: (opponentJoined: boolean) =>
    opponentJoined ? "Waiting for the server to verify both Cable Club saves…" : "Waiting for your opponent to join the lobby…",
  connecting: "Connecting to the link…",
  handshake: (stateLoaded: boolean) => (stateLoaded ? "Opponent's save loaded. Measuring the connection…" : "Waiting for your opponent to connect…"),
  running: (inputDelay: number) => `Linked · input delay ${inputDelay} frame${inputDelay === 1 ? "" : "s"}`,
  serverRunning: "Linked through the server",
  serverBadge: "Server battle",
  serverBadgeHelp: "The server runs both Game Boys, so each button press takes one round trip.",
  stalled: "Waiting for your opponent…",
  reconnecting: "Reconnecting to the server…",
  desync: "The two games went out of sync. The link is closing; nothing changes on chain.",
  ended: (outcome: LinkEndOutcome, reason: string) => {
    if (outcome === "done") return "Link finished. The server is checking the session.";
    if (outcome === "desync") return "The two games went out of sync, so the link closed. Nothing changes on chain.";
    if (reason === "peer_left") return "The link ended because a player left.";
    if (reason === "connection_lost") return "The connection to the link server was lost.";
    return "The link timed out.";
  },
  stuck: "If the game is still waiting for the other player, use Power cycle.",
  reload: "The server ran this link, so your game continues from your verified save. Reload to keep playing.",
  leave: "Leave link",
  cancel: "Cancel",
  dismiss: "Dismiss",
};

export function linkResultText(ev: Extract<SseEvent, { type: "link.result" }>): string {
  switch (ev.outcome) {
    case "completed":
      return ev.activity === "trade" ? "Trade completed. Both Pokémon have changed hands on chain." : "Link battle finished.";
    case "won":
      return "You won the link battle.";
    case "lost":
      return "You lost the link battle.";
    case "draw":
      return "The link battle was a draw.";
    case "desync":
      return "The link went out of sync, so nothing changed.";
    default:
      return ev.activity === "trade" ? "The trade didn't complete, so both players keep their Pokémon." : "The link battle didn't complete.";
  }
}

/** Toast for a purse credit written into the running game (§14.6). */
export function purseText(m: { amount: number; applied: number }): string {
  if (m.applied > 0) return `${formatRcdDelta(m.applied)} added to your purse`;
  if (m.applied < 0) return `${formatRcdDelta(m.applied)} from your purse`;
  return m.amount > 0 ? "Your purse is full; the rest stays in your in-game balance" : "Your purse is already empty";
}

export function autosaveText(m: { moneyDelta: number; newMons: AutosaveNewMon[]; rareFound: AutosaveRareFound[]; triggers: string[] }): string {
  const parts: string[] = [];
  if (m.triggers.includes("money") && m.moneyDelta !== 0) parts.push(formatRcdDelta(m.moneyDelta));
  for (const r of m.rareFound) parts.push(r.count > 1 ? `${r.name} ×${r.count}` : r.name);
  for (const mon of m.newMons) parts.push(`${mon.name} caught`);
  return parts.length ? `Auto-saved · ${parts.join(" · ")}` : "Auto-saved";
}
